import axios from "axios";
import { useAxiosHook } from "./useAxiosHook";
import { isFunction } from "lodash";
import { useDispatch, useStore } from "react-redux";

import { sortObjectWithArrays } from "../_src_shared/utils/functions/sortObjectWithArraysCaseSensitive";
import {
  fetchedSelectionsSetFileDocGroupAndTypes,
  fetchedSelectionsSetKey,
} from "../redux/reducers/fetchedSelectionsReducer";
import { usersActionSet } from "../redux/actions/usersActions";
import { cachedDataItemSet } from "../redux/actions/cachedDataActions";
import { CACHED_DATA_REDUCER_TYPES } from "../redux/reducers/cachedDataReducer";
import { nsKey } from "../utils/storage/partnerStorage";

const ROUTE_PREFIX_UNDERWRITING = "underwriting";

// ─── Reference-data cache (partner-scoped localStorage, 5-day TTL) ────────────
// File doc groups/types and model select-options change rarely, so cache them
// client-side and skip the network call on page load while the cache is fresh.
// Refetch only when the entry is missing or older than the TTL.
const SELECTION_CACHE_TTL_MS = 5 * 24 * 60 * 60 * 1000; // 5 days

const DOC_TYPES_CACHE_KEY = "fetchedSelections.fileDocGroupAndTypes";
// Bump this whenever the backend's doc-group / doc-type registry changes in a
// way every user must pick up immediately. A cached entry stamped with a
// different (or missing) version is discarded on first read, which forces one
// fresh GET /underwriting/file_doc_types per browser without renaming the
// storage key. History: 1 = unstamped original; 2 = 2026-09-15 backend
// registry update.
const DOC_TYPES_CACHE_VERSION = 2;
const MODEL_SCHEMA_CACHE_PREFIX = "fetchedSelections.modelSchema";
// The loan model backs most app dropdowns (loanStatus, pipeline, product type,
// purpose, amortization, etc.), so it's the default object to pull.
const DEFAULT_MODEL_OBJECT = "underwriting";

// `version` is optional: when given, an entry stamped with a different version
// (or none) is treated as stale and evicted, same as a TTL expiry.
const readSelectionCache = (cacheKey, version) => {
  try {
    const raw = localStorage.getItem(nsKey(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed?.savedAt) return null;
    const expired = Date.now() - parsed.savedAt > SELECTION_CACHE_TTL_MS;
    const outdated = version !== undefined && parsed.v !== version;
    if (expired || outdated) {
      localStorage.removeItem(nsKey(cacheKey));
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
};

const writeSelectionCache = (cacheKey, data, version) => {
  try {
    localStorage.setItem(
      nsKey(cacheKey),
      JSON.stringify({
        data,
        savedAt: Date.now(),
        ...(version !== undefined && { v: version }),
      }),
    );
  } catch {
    // ignore quota / serialization errors — cache is best-effort
  }
};

// ─── In-flight de-dupe guards (module-scoped, shared across hook instances) ───
// `users` / `correspondents` live ONLY in Redux (no localStorage TTL), so the
// "is it already loaded?" check is a Redux-presence read. These promises stop
// two components that mount in the same tick from both firing the same GET —
// the second caller awaits the first's request instead.
let usersFetchInFlight = null;
let correspondentsFetchInFlight = null;

export const useFetchedSelectionsRequestsHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;
  const dispatch = useDispatch();
  // Read-only access to the current store so `ensure*` can check whether the
  // data is already loaded WITHOUT subscribing the calling component to it.
  const store = useStore();

  // `force: true` bypasses the localStorage cache and always refetches.
  const getLoanFileDocGroupsAndTypes = async ({
    onSuccessFn,
    onFailFn,
    force = false,
  } = {}) => {
    // Fresh cache hit → hydrate Redux from it and skip the network call.
    if (!force) {
      const cached = readSelectionCache(DOC_TYPES_CACHE_KEY, DOC_TYPES_CACHE_VERSION);
      if (cached) {
        dispatch(fetchedSelectionsSetFileDocGroupAndTypes(cached));
        if (isFunction(onSuccessFn)) onSuccessFn(cached);
        return;
      }
    }

    const endpoint = `${ROUTE_PREFIX_UNDERWRITING}/file_doc_types`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Loan File Doc Groups And Types",
      path: endpoint,

      cancelSource: source,
      onSuccessFn: (d) => {
        const alphabetizedData = sortObjectWithArrays(d);

        writeSelectionCache(DOC_TYPES_CACHE_KEY, alphabetizedData, DOC_TYPES_CACHE_VERSION);
        if (isFunction(onSuccessFn)) {
          onSuccessFn(alphabetizedData);
        }
        dispatch(fetchedSelectionsSetFileDocGroupAndTypes(alphabetizedData));
      },
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  // All app users (B2BUser docs) backing every user dropdown / email-resolver
  // cell renderer AND the @-mention picker in comments. Served by
  // GET /users/all_users (B2BUser.get_all_users) and stored ONLY in the `users`
  // Redux slice — the single app-wide source anything reads from
  // (SelectUserEmailInput, CellRendererUserByEmail, the comment editor, etc.).
  // Intentionally NOT cached in localStorage so the list is always fresh and
  // there's one source of truth.
  //
  // Routed through the eastview-os integration backend (`type: "pipeline"`) —
  // the same host the loan pipeline fetch uses — since it accepts the new JWT
  // auth (the default RTL backend 403s under the auth swap).
  const getAllUsers = async ({ onSuccessFn, onFailFn } = {}) => {
    const source = axios.CancelToken.source();
    const params = {
      name: "Get All Users",
      path: "users/all_users",
      type: "pipeline",
      cancelSource: source,
      onSuccessFn: (d) => {
        const list = Array.isArray(d) ? d : [];
        dispatch(usersActionSet(list));
        // Mirror the list into the generic `cachedData` cache so consumers that
        // read reference data from there get it too (kept in sync with the
        // `users` slice every dropdown / email-resolver reads from).
        dispatch(cachedDataItemSet(CACHED_DATA_REDUCER_TYPES.USERS, list));
        if (isFunction(onSuccessFn)) onSuccessFn(list);
      },
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  // Model select-options / dropdown schema for a given object (loan model by
  // default). Served by GET /model_schemas/object/{objectType} — the response
  // ({ model_summary, nested_objects, enums, model_fields }) carries the
  // field-level select options. Cached per object type with the same 5-day TTL
  // as the doc types; `force: true` bypasses the cache.
  const getModelSelectOptions = async ({
    objectType = DEFAULT_MODEL_OBJECT,
    onSuccessFn,
    onFailFn,
    force = false,
  } = {}) => {
    const cacheKey = `${MODEL_SCHEMA_CACHE_PREFIX}.${objectType}`;
    const reduxKey = `modelSchema:${objectType}`;

    if (!force) {
      const cached = readSelectionCache(cacheKey);
      if (cached) {
        dispatch(fetchedSelectionsSetKey(reduxKey, cached));
        if (isFunction(onSuccessFn)) onSuccessFn(cached);
        return;
      }
    }

    const endpoint = `model_schemas/object/${objectType}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Model Select Options",
      path: endpoint,
      cancelSource: source,
      onSuccessFn: (d) => {
        writeSelectionCache(cacheKey, d);
        dispatch(fetchedSelectionsSetKey(reduxKey, d));
        if (isFunction(onSuccessFn)) onSuccessFn(d);
      },
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  // Correspondent objects for dropdowns (correspondent selection on a loan).
  // Served by GET /correspondents/list. Intentionally NOT cached in localStorage
  // (unlike the doc types / model schema) — it always fetches fresh and is only
  // stored in Redux under `fetchedSelections.correspondents`.
  const getCorrespondents = async ({ onSuccessFn, onFailFn } = {}) => {
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Correspondents",
      // eastview-os serves the list at /correspondents/list (not /correspondents).
      path: "correspondents/list",
      cancelSource: source,
      onSuccessFn: (d) => {
        const list = Array.isArray(d) ? d : [];
        dispatch(fetchedSelectionsSetKey("correspondents", list));
        // Mirror into the generic `cachedData` cache alongside the
        // `fetchedSelections.correspondents` slice the option hooks read from.
        dispatch(
          cachedDataItemSet(CACHED_DATA_REDUCER_TYPES.CORRESPONDENTS, list),
        );
        if (isFunction(onSuccessFn)) onSuccessFn(list);
      },
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  // ─── Lazy, cache-gated loaders ──────────────────────────────────────────────
  // Prefer these over getAllUsers / getCorrespondents at call sites. They fetch
  // ONLY when the data isn't already in Redux, so a screen can ask for what it
  // needs on mount without re-hitting the network on every navigation. Pages
  // that DON'T consume the data simply never call these, so e.g. the Support
  // page no longer pulls users + correspondents it never shows.
  //   1. Already in Redux  → resolve immediately, no network call.
  //   2. A fetch is in-flight → await that one (no duplicate GET).
  //   3. Otherwise          → fetch, which dispatches into Redux on success.
  // `force: true` always refetches (e.g. after a mutation that may stale it).
  const ensureAllUsers = ({ onSuccessFn, onFailFn, force = false } = {}) => {
    const existing = store.getState()?.users;
    if (!force && Array.isArray(existing) && existing.length > 0) {
      if (isFunction(onSuccessFn)) onSuccessFn(existing);
      return Promise.resolve(existing);
    }
    if (usersFetchInFlight) return usersFetchInFlight;
    usersFetchInFlight = Promise.resolve(
      getAllUsers({ onSuccessFn, onFailFn }),
    ).finally(() => {
      usersFetchInFlight = null;
    });
    return usersFetchInFlight;
  };

  const ensureCorrespondents = ({
    onSuccessFn,
    onFailFn,
    force = false,
  } = {}) => {
    const existing = store.getState()?.fetchedSelections?.correspondents;
    if (!force && Array.isArray(existing) && existing.length > 0) {
      if (isFunction(onSuccessFn)) onSuccessFn(existing);
      return Promise.resolve(existing);
    }
    if (correspondentsFetchInFlight) return correspondentsFetchInFlight;
    correspondentsFetchInFlight = Promise.resolve(
      getCorrespondents({ onSuccessFn, onFailFn }),
    ).finally(() => {
      correspondentsFetchInFlight = null;
    });
    return correspondentsFetchInFlight;
  };

  return {
    error,
    loading,

    getLoanFileDocGroupsAndTypes,
    getAllUsers,
    getModelSelectOptions,
    getCorrespondents,
    ensureAllUsers,
    ensureCorrespondents,
  };
};
